import * as z from "zod";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../providers/form";
import InputField from "widgets/shared/inputs/inputField";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import ComboBox from "widgets/shared/inputs/combobox";
import GenericButton from "widgets/controls/buttons/generic";
import { ReticulationTypeDropdown } from "models/dropdowns/reticulationTypes";
// Schema
const ReticulationSchema = z.object({
  _id: z.string().optional(),
  name: z.string().min(1, { message: i18next.t("zod.error.required") }),
  sites: z.any().optional(),
  reticulationType: z
    .string()
    .min(1, { message: i18next.t("zod.error.required") }),
  config: z.any().optional(),
});

export type ReticulationFormType = z.infer<typeof ReticulationSchema>;

// Props
type ReticulationFormProps = {
  submit: (data: ReticulationFormType) => void;
  onClose: () => void;
  defaultValues?: ReticulationFormType;
  values?: ReticulationFormType;
  loading?: boolean;
  context: "add" | "edit";
};

const ReticulationForm = ({
  submit,
  onClose,
  values,
  loading,
  context = "add",
}: ReticulationFormProps) => {
  const form = useForm<ReticulationFormType>({
    resolver: zodResolver(ReticulationSchema),
    defaultValues: {
      _id: undefined,
      name: "",
      sites: [],
      reticulationType: "",
      config: {},
    },
    values,
  });

  const { t } = useTranslation();
  const { errors, isDirty } = form.formState;

  const onSubmit = async (formData: ReticulationFormType) => {
    try {
      await submit(formData);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const clearErrors = () => {
    form.clearErrors();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 gap-3">
          <FormField
            control={form.control}
            name="name"
            rules={{ required: true }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.input.reticulation.name")}</FormLabel>
                <FormControl>
                  <InputField
                    placeholder={t("form.placeholder.reticulation.name")}
                    id="reticulation-form-name"
                    type="text"
                    state={errors.name ? "error" : undefined}
                    onFocus={clearErrors}
                    color="secondary"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="reticulationType"
            rules={{ required: true }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.input.reticulation.type")}</FormLabel>
                <FormControl>
                  <ComboBox
                    items={ReticulationTypeDropdown}
                    placeholder={t("form.placeholder.reticulation.type")}
                    // labelKey="name"
                    // valueKey="value"
                    id="reticulation-form-type"
                    type="text"
                    state={errors.reticulationType ? "error" : undefined}
                    onFocus={clearErrors}
                    color="secondary"
                    disabled={context === "edit"}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex justify-end pt-2">
          <div className="flex gap-2">
            <GenericButton
              onClick={onClose}
              text={t("button.action.cancel")}
              size="md"
              variant="ghost"
              color="secondary"
            />

            <GenericButton
              type="submit"
              text={t("button.action.ok")}
              // disabled={!isDirty}
              loading={loading || false}
              size="md"
            />
          </div>
        </div>
      </form>
    </Form>
  );
};

export default ReticulationForm;
